import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import './pages.css';

interface ExtractionFile {
  filename: string;
  size: number;
  created: string;
  modified: string;
}

interface ExtractionPreviewRow {
  [key: string]: string;
}

const Extraction: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [availableFiles, setAvailableFiles] = useState<ExtractionFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [previewColumns, setPreviewColumns] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<ExtractionPreviewRow[]>([]);

  const loadAvailableFiles = useCallback(async () => {
    setLoadingFiles(true);
    setFilesError(null);
    try {
      const response = await apiClient.get('/extraction/files/');
      setAvailableFiles(response.data.files || []);
    } catch (err: any) {
      setFilesError(err.response?.data?.error || 'Unable to load files. Please try again.');
      setAvailableFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    loadAvailableFiles();
  }, [loadAvailableFiles]);

  const runExtraction = async (selectedFile: File) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await apiClient.post('/extraction/extract/', formData);

      // Reload available files after successful extraction
      await loadAvailableFiles();

      // Debug: log the response
      console.log('Extraction response:', response.data);
      
      const previewCols = response.data?.preview_columns || [];
      const previewRowsData = response.data?.preview_rows || [];
      
      console.log('Preview columns:', previewCols);
      console.log('Preview rows:', previewRowsData);
      
      setPreviewColumns(previewCols);
      setPreviewRows(previewRowsData);
      setSuccess(true);
      setError(null);
    } catch (err: any) {
      console.error('Extraction error:', err);
      const errorData = err.response?.data;
      const statusCode = err.response?.status;

      // Build a detailed error message
      let errorMessage = 'An error occurred during extraction';

      // Handle 413 error (file too large) - can come from nginx or backend
      const is413Error = statusCode === 413 ||
        err.message?.includes('413') ||
        (typeof errorData === 'string' && errorData.includes('413'));

      if (is413Error) {
        errorMessage = 'The file you selected is too large to upload. The maximum file size is 300 MB.  Please try with a smaller file or reduce the file size before uploading.';
      } else if (errorData) {
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }

      setError(errorMessage);
      setSuccess(false);
      setPreviewColumns([]);
      setPreviewRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setError(null);
      setSuccess(false);
      setPreviewColumns([]);
      setPreviewRows([]);
      runExtraction(selectedFile);
    }
  };

  const handleDownload = async () => {
    if (!selectedFile) {
      setError('Please select a file to download');
      return;
    }

    try {
      const response = await apiClient.get(`/extraction/files/${selectedFile}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', selectedFile);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.error || 
        (err.response?.status === 404 
          ? `File "${selectedFile}" not found. It may have been deleted.`
          : 'An error occurred while downloading the file. Please try again.');
      setError(errorMessage);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="page-container">
      <h1 className="page-title">URS Extraction Tool</h1>
      <div className="upload-section">
        <label className="upload-label" htmlFor="file">
          Choose an URS file (PDF or DOCX) to start extraction immediately
        </label>
        <input
          id="file"
          type="file"
          accept=".pdf,.docx"
          onChange={handleFileChange}
          className="file-input"
          disabled={loading}
        />
      </div>

      <div className="page-description">
        <p>
          This application enables you to extract requirements from tables of a <strong>URS file</strong> (User Requirement Specifications).
          Requirements will be downloadable as an <strong>Excel</strong> file compatible with other steps of the tool.
          It supports <strong>PDF</strong> and <strong>docx</strong> files.
        </p>
        <h3>How to Use:</h3>
        <ol>
          <li><strong>Upload your file:</strong> Extraction starts automatically.</li>
          <li><strong>Wait for completion:</strong> Progress is shown below.</li>
          <li><strong>Select and download</strong> the generated Excel file from the list.</li>
        </ol>
      </div>

      {error && (
        <div className="error-message" style={{ whiteSpace: 'pre-line' }}>
          {error}
        </div>
      )}
      {success && <div className="success-message">Extraction completed successfully! Select a file from the list below to download.</div>}

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Processing your file...</p>
        </div>
      )}

      {!loading && (
        <div className="preview-section">
          <h2 className="preview-title">Latest Extraction Preview</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {(previewColumns.length > 0 ? previewColumns : ['id', 'requirements']).map((col) => (
                    <th key={col}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.length === 0 ? (
                  <tr>
                    <td className="empty" colSpan={previewColumns.length > 0 ? previewColumns.length : 2}>
                      {loading ? 'Processing...' : 'No data available. Upload a file to start extraction.'}
                    </td>
                  </tr>
                ) : (
                  previewRows.map((row, idx) => (
                    <tr key={idx}>
                      {previewColumns.map((col) => (
                        <td key={`${idx}-${col}`}>
                          {String(row[col] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="results-section">
        <h2 className="results-title">Available Extraction Results</h2>
        
        {filesError && (
          <div className="error-message" style={{ whiteSpace: 'pre-line' }}>
            {filesError}
          </div>
        )}

        {loadingFiles ? (
          <p>Loading files...</p>
        ) : availableFiles.length === 0 ? (
          <p className="text-secondary">No extraction results available yet.</p>
        ) : (
          <>
            <div className="upload-section">
              <label className="upload-label" htmlFor="fileSelect">
                Select a file to download
              </label>
              <select
                id="fileSelect"
                value={selectedFile}
                onChange={(e) => setSelectedFile(e.target.value)}
                className="file-input select-margin-bottom"
              >
                <option value="">Select a file...</option>
                {availableFiles.map((file) => (
                  <option key={file.filename} value={file.filename}>
                    {file.filename} ({formatFileSize(file.size)}) - {formatDate(file.modified)}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="button"
              onClick={handleDownload}
              disabled={!selectedFile}
            >
              Download Selected File
            </button>
          </>
        )}

        <button
          type="button"
          className="button button-refresh margin-top-medium"
          onClick={loadAvailableFiles}
        >
          Refresh List
        </button>
      </div>
    </div>
  );
};

export default Extraction;

